/**
 * One Lead Finder search: its progress while it runs, then its results to act
 * on — add as contacts (optionally assigned to a team member), add as vendors,
 * dismiss / restore — one by one or selected together. Filters (has a phone /
 * email / website, not yet a contact, a platform, the AI's fit score), search,
 * sort and a CSV export.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
  TextInput,
  RefreshControl,
  Linking,
  Modal,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { ScreenLayout, Header, FormHeader, ConfirmationModal } from '../components';
import { Button, Toggle } from '../components/design-system';
import { AiBotIcon } from '../components/icons/AppIcon';
import {
  ALL_PLATFORMS,
  Chip,
  PLATFORM_ICONS,
  ProgressBar,
  StatusBadge,
  categoryLabel,
  isActiveRun,
  PHASE_KEYS,
  regionLabel,
  setPendingClone,
  statusColor,
} from '../components/lead-finder/leadFinderUi';
import { AddToListModal } from '../components/lead-finder/AddToListModal';
import { apiService, BusinessUser, LeadOtherResult, LeadPlatform, LeadResult, LeadResultStatus, LeadResultsQuery, LeadRun } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useGoBack } from '../hooks/useGoBack';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { openNotificationTarget } from '../navigation/notificationRouter';
import type { TranslationKey } from '../i18n/en';

const PAGE_SIZE = 50;
const POLL_MS = 5000;
type Action = 'contact' | 'vendor' | 'dismiss' | 'restore' | 'keep';
type Tab = 'new' | 'contact' | 'vendor' | 'dismissed' | 'all';
const TABS: Tab[] = ['new', 'contact', 'vendor', 'dismissed', 'all'];

const resultStatusColor = (s: LeadResultStatus): string => ({
  new: colors.status.info,
  contact: colors.status.success,
  vendor: colors.primary,
  dismissed: colors.text.secondary,
}[s] as string);

interface Props {
  runId: string;
}

export const LeadRunScreen: React.FC<Props> = ({ runId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const goBack = useGoBack('lead-finder');
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const [run, setRun] = useState<(LeadRun & { counts: Record<string, number> }) | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [results, setResults] = useState<LeadResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [tab, setTab] = useState<Tab>('new');
  // The AI matches, or "other found" (what a check turned away; minimal details, Keep only).
  const [view, setView] = useState<'matches' | 'other'>('matches');
  const [otherTotal, setOtherTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [hasPhone, setHasPhone] = useState(false);
  const [hasMobile, setHasMobile] = useState(false);
  const [hasEmail, setHasEmail] = useState(false);
  const [hasWebsite, setHasWebsite] = useState(false);
  const [notExisting, setNotExisting] = useState(false);
  const [goodFit, setGoodFit] = useState(false);
  const [minScore, setMinScore] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [targetType, setTargetType] = useState<'any' | 'entity' | 'individual'>('any');
  const [customField, setCustomField] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [platform, setPlatform] = useState<LeadPlatform | undefined>();
  const [sort, setSort] = useState<'score' | 'name' | 'rating'>('score');
  const [showFilters, setShowFilters] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [acting, setActing] = useState<Action | null>(null);
  const [assignFor, setAssignFor] = useState<string[] | null>(null); // ids waiting for "add as contacts" with a person
  const [users, setUsers] = useState<BusinessUser[] | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [listFor, setListFor] = useState<string[] | null>(null); // ids waiting for "Add to list"
  const [showRerun, setShowRerun] = useState(false);
  const [onlyNew, setOnlyNew] = useState(true);
  const [rerunning, setRerunning] = useState(false);

  /** The same search again, as a new run. */
  const rerun = async () => {
    setRerunning(true);
    try {
      const res = await apiService.rerunLeadRun(runId, onlyNew);
      if (res.statusCode === 1000 && res.data) {
        setShowRerun(false);
        showNotification('success', t('leadFinder.title'), t('leadFinder.rerun.started'));
        navigateTo('lead-run', res.data.id);
      } else {
        showNotification('error', t('leadFinder.title'), res.errors?.[0]?.message || t('leadFinder.error.start'));
      }
    } catch (err: any) {
      showNotification('error', t('leadFinder.title'), err?.message || t('leadFinder.error.start'));
    } finally {
      setRerunning(false);
    }
  };

  /** Its settings in the New search form, to change and start as a new run. */
  const duplicate = () => {
    if (!run) return;
    setPendingClone(run);
    navigateTo('lead-finder');
  };

  const query = useMemo<LeadResultsQuery>(() => view === 'other' ? { view: 'other', ...(search.trim() ? { search: search.trim() } : {}) } : ({
    ...(tab !== 'all' ? { status: tab } : {}),
    ...(hasPhone ? { hasPhone: true } : {}),
    ...(hasMobile ? { hasMobile: true } : {}),
    ...(hasEmail ? { hasEmail: true } : {}),
    ...(hasWebsite ? { hasWebsite: true } : {}),
    ...(notExisting ? { existing: false } : {}),
    ...(minScore.trim() && Number.isFinite(Number(minScore)) ? { minScore: Math.max(0, Math.min(100, Number(minScore))) } : goodFit ? { minScore: 70 } : {}),
    ...(maxScore.trim() && Number.isFinite(Number(maxScore)) ? { maxScore: Math.max(0, Math.min(100, Number(maxScore))) } : {}),
    ...(targetType !== 'any' ? { targetType } : {}),
    ...(customField && customValue.trim() ? { customField, customValue: customValue.trim() } : {}),
    ...(platform ? { platform } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
    sort,
  }), [view, tab, hasPhone, hasMobile, hasEmail, hasWebsite, notExisting, goodFit, minScore, maxScore, targetType, customField, customValue, platform, search, sort]);

  const loadRun = useCallback(async () => {
    try {
      const res = await apiService.getLeadRun(runId);
      if (res.statusCode === 1000 && res.data) {
        setRun(res.data);
        setRunError(null);
      } else setRunError(res.errors?.[0]?.message || t('leadFinder.error.load'));
    } catch (err: any) {
      setRunError(err?.message || t('leadFinder.error.load'));
    }
  }, [runId, t]);

  const loadResults = useCallback(async (reset = true) => {
    try {
      const res = await apiService.getLeadResults(runId, { ...query, limit: PAGE_SIZE, skip: reset ? 0 : results.length });
      if (res.statusCode === 1000 && res.data) {
        const items = res.data.items || [];
        setResults((prev) => (reset ? items : [...prev, ...items]));
        setTotal(res.data.total || 0);
        setOtherTotal(res.data.otherTotal || 0);
      }
    } catch {
      // the run card shows load errors
    }
  }, [runId, query, results.length]);

  const reload = useCallback(async () => {
    await Promise.all([loadRun(), loadResults(true)]);
    setLoading(false);
    setRefreshing(false);
  }, [loadRun, loadResults]);

  useEffect(() => {
    if (!runId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, query]);

  // While the search runs, follow it (results arrive as they are kept).
  const active = !!run && isActiveRun(run.status);
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => { loadRun(); loadResults(true); }, POLL_MS);
    return () => clearInterval(timer);
  }, [active, loadRun, loadResults]);

  // Clear selection when the list changes underneath it.
  useEffect(() => { setSelected(new Set()); }, [query]);

  const toggleSelect = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = results.length > 0 && results.every((r) => selected.has(r.id));
  const selectAll = () => setSelected(allSelected ? new Set() : new Set(results.map((r) => r.id)));

  const act = async (action: Action, ids: string[], assignTo?: string) => {
    if (!ids.length) return;
    setActing(action);
    try {
      const res = await apiService.actOnLeadResults(runId, { action, ids, ...(assignTo ? { assignTo } : {}) });
      if (res.statusCode === 1000 && res.data) {
        const { done, failed } = res.data;
        if (failed?.length) {
          showNotification('warning', t('leadFinder.title'), t('leadFinder.act.partial', { done, failed: failed.length }) + (failed[0]?.reason ? ` — ${failed[0].reason}` : ''));
        } else {
          showNotification('success', t('leadFinder.title'), t(`leadFinder.act.done.${action}` as TranslationKey, { count: done }));
        }
        setSelected(new Set());
        await reload();
      } else {
        showNotification('error', t('leadFinder.title'), res.errors?.[0]?.message || t('leadFinder.error.act'));
      }
    } catch (err: any) {
      showNotification('error', t('leadFinder.title'), err?.message || t('leadFinder.error.act'));
    } finally {
      setActing(null);
    }
  };

  /** "Add as contacts": first ask whether to assign them to someone. */
  const askAssign = async (ids: string[]) => {
    setAssignFor(ids);
    if (users) return;
    try {
      const businessId = apiService.getBusinessIdFromToken();
      if (!businessId) { setUsers([]); return; }
      const res = await apiService.getBusinessUsers(businessId);
      const list = Array.isArray(res.data) ? res.data : [];
      setUsers(list.filter((u) => !u.isAiAgent && u.role !== 'ai_agent' && u.userStatus === 'active'));
    } catch {
      setUsers([]);
    }
  };

  const cancelRun = async () => {
    setCancelling(true);
    try {
      const res = await apiService.cancelLeadRun(runId);
      if (res.statusCode === 1000) showNotification('success', t('leadFinder.title'), t('leadFinder.run.cancelled'));
      else showNotification('error', t('leadFinder.title'), res.errors?.[0]?.message || t('leadFinder.error.act'));
      await loadRun();
    } catch (err: any) {
      showNotification('error', t('leadFinder.title'), err?.message || t('leadFinder.error.act'));
    } finally {
      setCancelling(false);
      setShowCancel(false);
    }
  };

  const exportCsv = async () => {
    if (Platform.OS !== 'web') {
      showNotification('info', t('leadFinder.title'), t('leadFinder.run.exportWebOnly'));
      return;
    }
    setExporting(true);
    try {
      const { blob, filename } = await apiService.exportLeadResults(runId);
      const { downloadBlob } = await import('../utils/csvExport');
      downloadBlob(blob, filename);
    } catch (err: any) {
      showNotification('error', t('leadFinder.title'), err?.message || t('leadFinder.error.export'));
    } finally {
      setExporting(false);
    }
  };

  const open = (url?: string) => { if (url) Linking.openURL(url).catch(() => {}); };
  const openMap = (r: LeadResult) => {
    const google = r.sources.find((s) => s.source === 'google_maps' && s.url)?.url;
    if (google) return open(google);
    if (r.location) open(`https://www.google.com/maps/search/?api=1&query=${r.location.latitude},${r.location.longitude}`);
  };

  // ── Pieces ────────────────────────────────────────────────────────────────

  const renderRunCard = () => {
    if (!run) return null;
    const p = run.progress || {};
    const counts = run.counts || {};
    return (
      <View style={styles.card}>
        <View style={[styles.rowBetween, { flexDirection: rtl.rowDirection }]}>
          <View style={[styles.row, styles.flex, { flexDirection: rtl.rowDirection }]}>
            {run.aiPlanned ? <AiBotIcon size={18} color={colors.primary as string} /> : null}
            <Text style={[styles.meta, styles.flex, { textAlign: rtl.textStart }]} numberOfLines={2}>
              {[regionLabel(run.params?.region), run.params?.keywords?.join(', '), run.params?.categories?.map(categoryLabel).join(', ')].filter(Boolean).join(' · ')}
            </Text>
          </View>
          <StatusBadge label={run.cancelRequested && run.status === 'running' ? t('leadFinder.run.stopping') : t(`leadFinder.status.${run.status}` as TranslationKey)} color={statusColor(run.status)} />
        </View>
        {run.brief ? <Text style={[styles.brief, { textAlign: rtl.textStart }]} numberOfLines={3}>“{run.brief}”</Text> : null}
        {run.status === 'partial' ? <Text style={styles.meta}>{t('leadFinder.run.shortfall', { count: p.kept || 0, target: run.allowance || run.params.limit })}</Text> : null}
        {active ? (
          <View style={styles.gapXs}>
            <ProgressBar value={run.allowance ? (p.kept || 0) / run.allowance : 0} />
            <Text style={styles.meta}>
              {run.cancelRequested ? t('leadFinder.run.stopping') : run.phase && PHASE_KEYS[run.phase] ? t(PHASE_KEYS[run.phase]) : t('leadFinder.status.queued')}
            </Text>
          </View>
        ) : null}
        <View style={[styles.stats, { flexDirection: rtl.rowDirection }]}>
          {[
            { label: t('leadFinder.stat.kept'), value: p.kept || 0 },
            { label: t('leadFinder.stat.found'), value: p.found || 0 },
            { label: t('leadFinder.stat.duplicates'), value: p.duplicates || 0 },
            { label: t('leadFinder.stat.rejected'), value: p.rejected || 0 },
            { label: t('leadFinder.stat.enriched'), value: p.enriched || 0 },
            { label: t('leadFinder.stat.existing'), value: p.existing || 0 },
            ...(run.params?.onlyNew ? [{ label: t('leadFinder.stat.previous'), value: p.previous || 0 }] : []),
          ].map((s) => (
            <View key={s.label} style={styles.stat}>
              <Text style={styles.statValue}>{s.value}</Text>
              <Text style={styles.statLabel}>{s.label}</Text>
            </View>
          ))}
        </View>
        {p.rejectionReasons && Object.keys(p.rejectionReasons).length ? (
          <Text style={[styles.meta, { textAlign: rtl.textStart }]}>
            {Object.entries(p.rejectionReasons).map(([reason, count]) => `${t(`leadFinder.rejection.${reason}` as TranslationKey)}: ${count}`).join(' · ')}
          </Text>
        ) : null}
        {p.bySource && Object.keys(p.bySource).length ? (
          <Text style={[styles.meta, { textAlign: rtl.textStart }]}>
            {Object.entries(p.bySource).map(([s, n]) => `${t(`leadFinder.source.${s}` as TranslationKey)}: ${n}`).join(' · ')}
          </Text>
        ) : null}
        {run.notes?.map((n, i) => (
          <View key={i} style={[styles.row, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="information-circle-outline" size={14} color={colors.text.secondary as string} />
            <Text style={[styles.meta, styles.flex, { textAlign: rtl.textStart }]}>{n}</Text>
          </View>
        ))}
        {run.error ? <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{run.error}</Text> : null}
        <View style={[styles.runButtons, { flexDirection: rtl.rowDirection }]}>
          {!active ? (
            <Button title={t('leadFinder.rerun.action')} onPress={() => { setOnlyNew(true); setShowRerun(true); }} variant="primary" size="sm" />
          ) : null}
          <Button title={t('leadFinder.duplicate.action')} onPress={duplicate} variant="outline" size="sm" />
        </View>
        {Object.keys(counts).length ? (
          <Text style={[styles.meta, { textAlign: rtl.textStart }]}>
            {(['contact', 'vendor', 'dismissed'] as const).filter((k) => counts[k]).map((k) => `${t(`leadFinder.tab.${k}` as TranslationKey)}: ${counts[k]}`).join(' · ')}
          </Text>
        ) : null}
      </View>
    );
  };

  const switchView = (next: 'matches' | 'other') => { setSelected(new Set()); setShowFilters(false); setView(next); };

  const renderToolbar = () => (
    <View style={styles.toolbar}>
      <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
        <Chip label={t('leadFinder.view.matches') + (run?.progress?.kept ? ` (${run.progress.kept})` : '')} icon="sparkles-outline" selected={view === 'matches'} onPress={() => switchView('matches')} />
        <Chip label={t('leadFinder.view.other') + (otherTotal ? ` (${otherTotal})` : '')} icon="albums-outline" selected={view === 'other'} onPress={() => switchView('other')} />
      </View>
      {view === 'other' ? <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{t('leadFinder.view.otherHint')}</Text> : null}
      {view === 'matches' ? <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
        {TABS.map((k) => (
          <Chip
            key={k}
            label={t(`leadFinder.tab.${k}` as TranslationKey) + (k !== 'all' && run?.counts?.[k] ? ` (${run.counts[k]})` : '')}
            selected={tab === k}
            onPress={() => setTab(k)}
          />
        ))}
      </ScrollView> : null}
      <View style={[styles.searchRow, { flexDirection: rtl.rowDirection }]}>
        <View style={[styles.searchBox, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="search" size={16} color={colors.text.secondary as string} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder={t('leadFinder.results.search')}
            placeholderTextColor={colors.text.secondary as string}
            style={[styles.searchInput, { textAlign: rtl.textStart }]}
          />
        </View>
        {view === 'matches' ? (
          <TouchableOpacity style={[styles.iconButton, showFilters && styles.iconButtonOn]} onPress={() => setShowFilters((v) => !v)}>
            <Ionicons name="options-outline" size={18} color={(showFilters ? colors.text.onPrimary : colors.text.primary) as string} />
          </TouchableOpacity>
        ) : null}
      </View>
      {showFilters ? (
        <View style={styles.gapXs}>
          <Text style={styles.meta}>{t('leadFinder.criteria.scoreRange')}</Text>
          <View style={[styles.searchRow, { flexDirection: rtl.rowDirection }]}>
            <TextInput value={minScore} onChangeText={value => { setMinScore(value); setGoodFit(false); }} keyboardType="numeric" maxLength={3} placeholder={t('leadFinder.criteria.min')} placeholderTextColor={colors.text.secondary as string} style={[styles.searchBox, styles.searchInput]} />
            <TextInput value={maxScore} onChangeText={setMaxScore} keyboardType="numeric" maxLength={3} placeholder={t('leadFinder.criteria.max')} placeholderTextColor={colors.text.secondary as string} style={[styles.searchBox, styles.searchInput]} />
          </View>
          <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
            {(['any', 'entity', 'individual'] as const).map(type => <Chip key={type} label={t(`leadFinder.target.${type}` as TranslationKey)} selected={targetType === type} onPress={() => setTargetType(type)} />)}
          </View>
          {run?.params.requestedFields?.length ? <>
            <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
              {run.params.requestedFields.map(field => <Chip key={field.key} label={field.label} selected={customField === field.key} onPress={() => { setCustomField(customField === field.key ? '' : field.key); setCustomValue(''); }} />)}
            </View>
            {customField ? <TextInput value={customValue} onChangeText={setCustomValue} placeholder={t('leadFinder.criteria.filterValue')} placeholderTextColor={colors.text.secondary as string} style={[styles.searchBox, styles.searchInput]} /> : null}
          </> : null}
          <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
            <Chip label={t('leadFinder.filter.hasPhone')} icon="call-outline" selected={hasPhone} onPress={() => setHasPhone((v) => !v)} />
            <Chip label={t('leadFinder.filter.hasMobile')} icon="phone-portrait-outline" selected={hasMobile} onPress={() => setHasMobile((v) => !v)} />
            <Chip label={t('leadFinder.filter.hasEmail')} icon="mail-outline" selected={hasEmail} onPress={() => setHasEmail((v) => !v)} />
            <Chip label={t('leadFinder.filter.hasWebsite')} icon="globe-outline" selected={hasWebsite} onPress={() => setHasWebsite((v) => !v)} />
            <Chip label={t('leadFinder.filter.notExisting')} icon="person-add-outline" selected={notExisting} onPress={() => setNotExisting((v) => !v)} />
            <Chip label={t('leadFinder.filter.goodFit')} icon="star-outline" selected={goodFit} onPress={() => { setGoodFit((v) => !v); setMinScore(''); }} />
          </View>
          <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
            {ALL_PLATFORMS.map((p) => (
              <Chip key={p} label={t(`leadFinder.platform.${p}` as TranslationKey)} icon={PLATFORM_ICONS[p]} selected={platform === p} onPress={() => setPlatform(platform === p ? undefined : p)} />
            ))}
          </View>
          <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
            <Text style={styles.meta}>{t('leadFinder.sort.title')}</Text>
            {(['score', 'rating', 'name'] as const).map((k) => (
              <Chip key={k} label={t(`leadFinder.sort.${k}` as TranslationKey)} selected={sort === k} onPress={() => setSort(k)} />
            ))}
          </View>
        </View>
      ) : null}
      <View style={[styles.rowBetween, { flexDirection: rtl.rowDirection }]}>
        <TouchableOpacity style={[styles.row, { flexDirection: rtl.rowDirection }]} onPress={selectAll} disabled={!results.length}>
          <Ionicons name={allSelected ? 'checkbox' : 'square-outline'} size={18} color={colors.primary as string} />
          <Text style={styles.meta}>{t('leadFinder.results.selectAll')}</Text>
        </TouchableOpacity>
        <Text style={styles.meta}>{t('leadFinder.results.count', { count: total })}</Text>
      </View>
    </View>
  );

  /** "Other found": who it is and why it was turned away; Keep makes it a match with its full details. */
  const renderOther = ({ item: r }: { item: LeadOtherResult }) => {
    const isSelected = selected.has(r.id);
    return (
      <View style={[styles.card, styles.resultCard, isSelected && styles.resultSelected]}>
        <View style={[styles.resultHead, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity onPress={() => toggleSelect(r.id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name={isSelected ? 'checkbox' : 'square-outline'} size={20} color={colors.primary as string} />
          </TouchableOpacity>
          <View style={styles.flex}>
            <Text style={[styles.resultName, { textAlign: rtl.textStart }]} numberOfLines={2}>{r.name}</Text>
            <Text style={[styles.meta, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {[r.profession, r.category ? categoryLabel(r.category) : null, r.city || r.country].filter(Boolean).join(' · ')}
            </Text>
          </View>
          <Button title={t('leadFinder.act.keep')} onPress={() => act('keep', [r.id])} variant="outline" size="sm" loading={acting === 'keep' && selected.size === 0} disabled={!!acting} />
        </View>
        <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
          {r.reason ? <StatusBadge label={t(`leadFinder.rejection.${r.reason}` as TranslationKey) + (typeof r.aiScore === 'number' && r.reason === 'below_min_score' ? ` (${r.aiScore})` : '')} color={colors.status.warning as string} /> : null}
          {r.source ? <StatusBadge label={t(`leadFinder.source.${r.source}` as TranslationKey)} color={colors.text.secondary as string} /> : null}
        </View>
        {r.aiSummary ? <Text style={[styles.meta, { textAlign: rtl.textStart }]} numberOfLines={2}>{r.aiSummary}</Text> : null}
      </View>
    );
  };

  const renderResult = ({ item: r }: { item: LeadResult }) => {
    const isSelected = selected.has(r.id);
    const place = [r.address, r.city].filter(Boolean).join(', ');
    return (
      <View style={[styles.card, styles.resultCard, isSelected && styles.resultSelected]}>
        <View style={[styles.resultHead, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity onPress={() => toggleSelect(r.id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name={isSelected ? 'checkbox' : 'square-outline'} size={20} color={colors.primary as string} />
          </TouchableOpacity>
          <View style={styles.flex}>
            <Text style={[styles.resultName, { textAlign: rtl.textStart }]} numberOfLines={2}>{r.name}</Text>
            <Text style={[styles.meta, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {[r.profession, r.category ? categoryLabel(r.category) : null, place].filter(Boolean).join(' · ')}
            </Text>
          </View>
          {typeof r.aiScore === 'number' ? (
            <View style={[styles.score, { borderColor: r.aiScore >= 70 ? (colors.status.success as string) : r.aiScore >= 40 ? (colors.status.warning as string) : (colors.border as string) }]}>
              <Text style={styles.scoreText}>{r.aiScore}</Text>
            </View>
          ) : null}
        </View>

        <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
          {r.entityType && r.entityType !== 'unknown' && r.entityType !== 'any' ? <StatusBadge label={t(`leadFinder.target.${r.entityType}` as TranslationKey)} color={colors.status.info as string} /> : null}
          {r.scoreMethod ? <StatusBadge label={t(`leadFinder.criteria.method.${r.scoreMethod}` as TranslationKey)} color={colors.text.secondary as string} /> : null}
          {r.status !== 'new' ? <StatusBadge label={t(`leadFinder.result.${r.status}` as TranslationKey)} color={resultStatusColor(r.status)} /> : null}
          {r.dataListIds?.length ? <StatusBadge label={t('leadFinder.result.inLists', { count: r.dataListIds.length })} color={colors.status.info as string} /> : null}
          {r.platformMatch ? <StatusBadge label={t('leadFinder.result.platformMatch')} color={colors.status.success as string} /> : null}
          {r.existingContactId && r.status === 'new' ? <StatusBadge label={t('leadFinder.result.existingContact')} color={colors.status.warning as string} /> : null}
          {r.existingVendorId && r.status === 'new' ? <StatusBadge label={t('leadFinder.result.existingVendor')} color={colors.status.warning as string} /> : null}
          {typeof r.rating === 'number' ? (
            <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="star" size={12} color={colors.status.warning as string} />
              <Text style={styles.meta}>{r.rating}{r.reviews ? ` (${r.reviews})` : ''}</Text>
            </View>
          ) : null}
        </View>

        {r.aiSummary ? <Text style={[styles.brief, { textAlign: rtl.textStart }]} numberOfLines={2}>{r.aiSummary}</Text> : null}
        {r.aiOpener ? (
          <View style={[styles.opener, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="chatbubble-ellipses-outline" size={14} color={colors.primary as string} />
            <Text style={[styles.openerText, { textAlign: rtl.textStart }]} numberOfLines={3}>{r.aiOpener}</Text>
            <TouchableOpacity
              onPress={async () => { await Clipboard.setStringAsync(r.aiOpener || ''); showNotification('success', t('leadFinder.opener.title'), t('leadFinder.opener.copied')); }}
              accessibilityLabel={t('leadFinder.opener.copy')}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="copy-outline" size={16} color={colors.primary as string} />
            </TouchableOpacity>
          </View>
        ) : null}
        {(run?.params.requestedFields || []).filter(field => r.customFields?.[field.key] !== undefined).map(field => (
          <Text key={field.key} style={[styles.meta, { textAlign: rtl.textStart }]}>{field.label}: {String(r.customFields![field.key])}</Text>
        ))}
        {r.description && !r.aiSummary ? <Text style={[styles.meta, { textAlign: rtl.textStart }]} numberOfLines={2}>{r.description}</Text> : null}

        <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
          {r.phones.slice(0, 3).map((ph) => {
            const mobile = r.mobiles?.includes(ph), landline = r.landlines?.includes(ph);
            const kind = mobile && !landline ? 'mobile' : landline && !mobile ? 'landline' : null;
            return (
              <TouchableOpacity key={ph} style={[styles.contactPill, { flexDirection: rtl.rowDirection }]} onPress={() => open(`tel:${ph}`)}>
                <Ionicons name={kind === 'mobile' ? 'phone-portrait-outline' : 'call-outline'} size={13} color={colors.primary as string} />
                <Text style={styles.contactText}>{ph}</Text>
                {kind ? <Text style={styles.phoneKind}>{t(`leadFinder.phoneType.${kind}` as TranslationKey)}</Text> : null}
              </TouchableOpacity>
            );
          })}
          {r.emails.slice(0, 1).map((em) => (
            <TouchableOpacity key={em} style={[styles.contactPill, { flexDirection: rtl.rowDirection }]} onPress={() => open(`mailto:${em}`)}>
              <Ionicons name="mail-outline" size={13} color={colors.primary as string} />
              <Text style={styles.contactText} numberOfLines={1}>{em}</Text>
            </TouchableOpacity>
          ))}
          {r.website ? (
            <TouchableOpacity style={styles.iconPill} onPress={() => open(r.website)}>
              <Ionicons name="globe-outline" size={15} color={colors.primary as string} />
            </TouchableOpacity>
          ) : null}
          {(Object.entries(r.socials) as Array<[LeadPlatform, string]>).map(([p, url]) => (
            <TouchableOpacity key={p} style={styles.iconPill} onPress={() => open(url)}>
              <Ionicons name={PLATFORM_ICONS[p] || 'link-outline'} size={15} color={colors.primary as string} />
            </TouchableOpacity>
          ))}
          {r.location || r.sources.some((s) => s.source === 'google_maps') ? (
            <TouchableOpacity style={styles.iconPill} onPress={() => openMap(r)}>
              <Ionicons name="location-outline" size={15} color={colors.primary as string} />
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
          {r.status === 'new' ? (
            <>
              <TouchableOpacity style={[styles.actionButton, { flexDirection: rtl.rowDirection }]} onPress={() => askAssign([r.id])} disabled={!!acting}>
                <Ionicons name="person-add-outline" size={15} color={colors.primary as string} />
                <Text style={styles.actionText}>{r.existingContactId ? t('leadFinder.act.linkContact') : t('leadFinder.act.contact')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.actionButton, { flexDirection: rtl.rowDirection }]} onPress={() => act('vendor', [r.id])} disabled={!!acting}>
                <Ionicons name="business-outline" size={15} color={colors.primary as string} />
                <Text style={styles.actionText}>{t('leadFinder.act.vendor')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.actionButton, { flexDirection: rtl.rowDirection }]} onPress={() => setListFor([r.id])} disabled={!!acting}>
                <Ionicons name="list-outline" size={15} color={colors.primary as string} />
                <Text style={styles.actionText}>{t('leadFinder.act.list')}</Text>
              </TouchableOpacity>
              <View style={styles.flex} />
              <TouchableOpacity style={styles.dismissButton} onPress={() => act('dismiss', [r.id])} disabled={!!acting} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name="close-circle-outline" size={18} color={colors.text.secondary as string} />
              </TouchableOpacity>
            </>
          ) : r.status === 'dismissed' ? (
            <TouchableOpacity style={[styles.actionButton, { flexDirection: rtl.rowDirection }]} onPress={() => act('restore', [r.id])} disabled={!!acting}>
              <Ionicons name="arrow-undo-outline" size={15} color={colors.primary as string} />
              <Text style={styles.actionText}>{t('leadFinder.act.restore')}</Text>
            </TouchableOpacity>
          ) : r.status === 'contact' && r.contactId ? (
            <TouchableOpacity style={[styles.actionButton, { flexDirection: rtl.rowDirection }]} onPress={() => openNotificationTarget(navigateTo, `contact:${r.contactId}`, { channel: 'whatsapp' })}>
              <Ionicons name="chatbubble-ellipses-outline" size={15} color={colors.primary as string} />
              <Text style={styles.actionText}>{t('leadFinder.act.message')}</Text>
            </TouchableOpacity>
          ) : r.status === 'vendor' ? (
            <TouchableOpacity style={[styles.actionButton, { flexDirection: rtl.rowDirection }]} onPress={() => navigateTo('vendors')}>
              <Ionicons name="business-outline" size={15} color={colors.primary as string} />
              <Text style={styles.actionText}>{t('leadFinder.act.openVendors')}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    );
  };

  const renderBulkBar = () => {
    if (!selected.size) return null;
    const ids = [...selected];
    const anyDismissed = results.some((r) => selected.has(r.id) && r.status === 'dismissed');
    return (
      <View style={[styles.bulkBar, { flexDirection: rtl.rowDirection }]}>
        <Text style={styles.bulkText}>{t('leadFinder.results.selected', { count: selected.size })}</Text>
        <View style={styles.flex} />
        {view === 'other' ? (
          <Button title={t('leadFinder.act.keep')} onPress={() => act('keep', ids)} variant="primary" size="sm" loading={acting === 'keep'} disabled={!!acting} />
        ) : anyDismissed ? (
          <Button title={t('leadFinder.act.restore')} onPress={() => act('restore', ids)} variant="outline" size="sm" loading={acting === 'restore'} disabled={!!acting} />
        ) : (
          <>
            <Button title={t('leadFinder.act.contacts')} onPress={() => askAssign(ids)} variant="primary" size="sm" loading={acting === 'contact'} disabled={!!acting} />
            <Button title={t('leadFinder.act.vendors')} onPress={() => act('vendor', ids)} variant="outline" size="sm" loading={acting === 'vendor'} disabled={!!acting} />
            <Button title={t('leadFinder.act.toList')} onPress={() => setListFor(ids)} variant="outline" size="sm" disabled={!!acting} />
            <Button title={t('leadFinder.act.dismiss')} onPress={() => act('dismiss', ids)} variant="ghost" size="sm" loading={acting === 'dismiss'} disabled={!!acting} />
          </>
        )}
      </View>
    );
  };

  const userName = (u: BusinessUser) => `${u.user?.firstName || ''} ${u.user?.lastName || ''}`.trim() || u.user?.email || u.userId;

  const renderAssignModal = () => (
    <Modal visible={!!assignFor} transparent animationType="fade" onRequestClose={() => setAssignFor(null)}>
      <View style={styles.modalBackdrop}>
        <View style={styles.modal}>
          <Text style={styles.cardTitle}>{t('leadFinder.assign.title', { count: assignFor?.length || 0 })}</Text>
          <Text style={styles.meta}>{t('leadFinder.assign.hint')}</Text>
          <TouchableOpacity style={[styles.userRow, { flexDirection: rtl.rowDirection }]} onPress={() => { const ids = assignFor || []; setAssignFor(null); act('contact', ids); }}>
            <Ionicons name="people-outline" size={18} color={colors.text.secondary as string} />
            <Text style={styles.userName}>{t('leadFinder.assign.nobody')}</Text>
          </TouchableOpacity>
          {users === null ? <ActivityIndicator color={colors.primary} /> : (
            <ScrollView style={styles.userList}>
              {users.map((u) => (
                <TouchableOpacity key={u.userId} style={[styles.userRow, { flexDirection: rtl.rowDirection }]} onPress={() => { const ids = assignFor || []; setAssignFor(null); act('contact', ids, u.userId); }}>
                  <Ionicons name="person-circle-outline" size={18} color={colors.primary as string} />
                  <Text style={styles.userName}>{userName(u)}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}
          <Button title={t('common.cancel')} onPress={() => setAssignFor(null)} variant="ghost" />
        </View>
      </View>
    </Modal>
  );

  const renderEmpty = () => (
    <View style={styles.center}>
      {active ? <ActivityIndicator color={colors.primary} /> : <Ionicons name="search-outline" size={48} color={colors.text.secondary as string} />}
      <Text style={styles.emptyText}>{active ? t('leadFinder.results.waiting') : t('leadFinder.results.empty')}</Text>
    </View>
  );

  const body = () => {
    if (loading) return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
    if (runError && !run) {
      return (
        <View style={styles.center}>
          <Ionicons name="alert-circle-outline" size={56} color={colors.error} />
          <Text style={styles.emptyText}>{runError}</Text>
          <Button title={t('common.retry')} onPress={() => { setLoading(true); reload(); }} variant="primary" />
        </View>
      );
    }
    return (
      <FlatList
        data={results}
        keyExtractor={(r) => r.id}
        renderItem={view === 'other' ? (renderOther as any) : renderResult}
        ListHeaderComponent={<View style={styles.gapMd}>{renderRunCard()}{renderToolbar()}</View>}
        ListEmptyComponent={renderEmpty}
        contentContainerStyle={styles.content}
        onEndReached={() => {
          if (loadingMore || results.length >= total) return;
          setLoadingMore(true);
          loadResults(false).finally(() => setLoadingMore(false));
        }}
        onEndReachedThreshold={0.4}
        ListFooterComponent={loadingMore ? <ActivityIndicator color={colors.primary} /> : null}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); reload(); }} colors={[colors.primary as string]} />}
      />
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      formHeader={
        <FormHeader
          title={run?.name || t('leadFinder.title')}
          subtitle={run ? t(`leadFinder.status.${run.status}` as TranslationKey) : undefined}
          onBack={goBack}
          actions={[
            ...(active && !run?.cancelRequested ? [{ icon: 'stop-circle-outline' as const, onPress: () => setShowCancel(true), label: t('leadFinder.run.cancel') }] : []),
            ...(run && !active ? [{ icon: 'refresh-circle-outline' as const, onPress: () => { setOnlyNew(true); setShowRerun(true); }, label: t('leadFinder.rerun.action') }] : []),
            ...(run ? [{ icon: 'copy-outline' as const, onPress: duplicate, label: t('leadFinder.duplicate.action') }] : []),
            { icon: exporting ? ('hourglass-outline' as const) : ('download-outline' as const), onPress: exportCsv, label: t('leadFinder.run.export') },
            { icon: 'refresh' as const, onPress: () => { setRefreshing(true); reload(); }, label: t('leadFinder.refresh') },
          ]}
        />
      }
    >
      <View style={styles.container}>
        {body()}
        {renderBulkBar()}
      </View>
      {renderAssignModal()}
      <AddToListModal
        visible={!!listFor}
        runId={runId}
        ids={listFor || []}
        onClose={() => setListFor(null)}
        onDone={(text, failed) => {
          setListFor(null);
          setSelected(new Set());
          showNotification(failed ? 'warning' : 'success', t('leadFinder.title'), failed ? `${text} · ${t('leadFinder.addList.failed', { count: failed })}` : text);
          reload();
        }}
      />
      <Modal visible={showRerun} transparent animationType="fade" onRequestClose={() => setShowRerun(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modal}>
            <Text style={styles.cardTitle}>{t('leadFinder.rerun.title')}</Text>
            <Text style={styles.meta}>{t('leadFinder.rerun.hint')}</Text>
            <Toggle value={onlyNew} onValueChange={setOnlyNew} label={t('leadFinder.rerun.onlyNew')} description={t('leadFinder.rerun.onlyNewHint')} />
            <Button title={t('leadFinder.rerun.start')} onPress={rerun} variant="primary" loading={rerunning} disabled={rerunning} />
            <Button title={t('common.cancel')} onPress={() => setShowRerun(false)} variant="ghost" />
          </View>
        </View>
      </Modal>
      <ConfirmationModal
        visible={showCancel}
        onClose={() => setShowCancel(false)}
        onConfirm={cancelRun}
        title={t('leadFinder.run.cancelTitle')}
        message={t('leadFinder.run.cancelMessage')}
        confirmText={t('leadFinder.run.cancel')}
        cancelText={t('common.cancel')}
        type="warning"
        loading={cancelling}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  content: { padding: spacing.md, paddingBottom: Platform.OS !== 'web' ? 160 : 96, gap: spacing.sm, maxWidth: 960, width: '100%', alignSelf: 'center' },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    gap: spacing.sm,
    ...designTokens.shadows.sm,
  },
  cardTitle: { fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  row: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  rowBetween: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  wrapRow: { flexWrap: 'wrap', gap: spacing.xs, alignItems: 'center' },
  flex: { flex: 1 },
  gapXs: { gap: spacing.xs },
  gapMd: { gap: spacing.md, marginBottom: spacing.sm },
  meta: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  brief: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.regular, fontStyle: 'italic' },
  opener: { alignItems: 'flex-start', gap: spacing.xs, padding: spacing.sm, borderRadius: 8, backgroundColor: colors.background.secondary },
  openerText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  errorText: { fontSize: typography.fontSize.sm, color: colors.error, fontFamily: typography.fontFamily.regular },
  stats: { flexWrap: 'wrap', gap: spacing.md },
  runButtons: { gap: spacing.sm, flexWrap: 'wrap', paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border },
  stat: { minWidth: 72 },
  statValue: { fontSize: typography.fontSize.lg, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  statLabel: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  toolbar: { gap: spacing.sm },
  searchRow: { alignItems: 'center', gap: spacing.sm },
  searchBox: {
    flex: 1,
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchInput: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular, paddingVertical: 0 },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
  },
  iconButtonOn: { backgroundColor: colors.primary, borderColor: colors.primary },
  resultCard: { gap: spacing.xs },
  resultSelected: { borderWidth: 1, borderColor: colors.primary },
  resultHead: { alignItems: 'flex-start', gap: spacing.sm },
  resultName: { fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  score: { width: 36, height: 36, borderRadius: 18, borderWidth: 2, justifyContent: 'center', alignItems: 'center' },
  scoreText: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  contactPill: {
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: String(colors.primary) + '12',
    maxWidth: 260,
  },
  contactText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  phoneKind: { fontSize: 10, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  iconPill: { width: 28, height: 28, borderRadius: 14, backgroundColor: String(colors.primary) + '12', justifyContent: 'center', alignItems: 'center' },
  actions: { alignItems: 'center', gap: spacing.sm, marginTop: spacing.xs, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border },
  actionButton: {
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: String(colors.primary) + '10',
  },
  actionText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  dismissButton: { padding: spacing.xs },
  bulkBar: {
    position: 'absolute',
    left: spacing.md,
    right: spacing.md,
    bottom: Platform.OS !== 'web' ? 90 : spacing.md,
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
    padding: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.primary,
    ...designTokens.shadows.md,
  },
  bulkText: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  modalBackdrop: { flex: 1, backgroundColor: colors.background.overlay, justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  modal: {
    width: '100%',
    maxWidth: 420,
    maxHeight: '80%',
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  userList: { maxHeight: 320 },
  userRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  userName: { fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xxl, gap: spacing.sm },
  emptyText: { fontSize: typography.fontSize.base, color: colors.text.secondary, fontFamily: typography.fontFamily.regular, textAlign: 'center' },
});
